/**
 * Background-flush effects.
 *
 * While the recording is active (`recording` or `paused`), browser-level
 * background events fire chunk uploads + IDB persistence so an unexpected
 * tab close / OS suspend doesn't lose audio:
 *
 *   - beforeunload     — show the "are you sure?" prompt
 *   - visibilitychange — when the tab goes hidden, flush pending chunks +
 *                        persist current chunk buffer to IDB
 *   - pagehide         — last-ditch keepalive POST of unsent chunks via
 *                        raw fetch (Eden Treaty doesn't support t.File())
 */
import { useEffect } from "react"
import { saveChunksToIDB } from "../../../hooks/useIndexedDB"
import { apiUrl } from "../../../lib/runtime-origin"
import type { IRemoteUploadHandle, TRecordingState } from "../interfaces"

interface IProps {
  state: TRecordingState
  meetingId: string
  subject?: string
  templateId?: string
  chunksRef: React.MutableRefObject<Blob[]>
  activeMimeTypeRef: React.MutableRefObject<string>
  startTimeRef: React.MutableRefObject<number>
  lastUploadedChunkIndexRef: React.MutableRefObject<number>
  uploadBatchIndexRef: React.MutableRefObject<number>
  absoluteBaseRef: React.MutableRefObject<number>
  provisionalPendingRef: React.MutableRefObject<boolean>
  doChunkUpload: (isFinal: boolean) => Promise<boolean>
  /** Desktop hybrid far-end channel — gets the same hide/unload keepalive. */
  remoteUpload?: IRemoteUploadHandle
}

export function useBackgroundFlush(props: IProps): void {
  const {
    state,
    meetingId,
    subject,
    templateId,
    chunksRef,
    activeMimeTypeRef,
    startTimeRef,
    lastUploadedChunkIndexRef,
    uploadBatchIndexRef,
    absoluteBaseRef,
    provisionalPendingRef,
    doChunkUpload,
    remoteUpload,
  } = props

  useEffect(() => {
    if (state !== "recording" && state !== "paused") return
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [state])

  // biome-ignore lint/correctness/useExhaustiveDependencies: provisionalPendingRef is a stable ref
  useEffect(() => {
    if (state !== "recording" && state !== "paused") return
    const handler = async (): Promise<void> => {
      if (document.visibilityState !== "hidden") return
      const newChunks = chunksRef.current.slice(lastUploadedChunkIndexRef.current)
      if (newChunks.length > 0) {
        // doChunkUpload is the SOLE owner of lastUploadedChunkIndexRef /
        // uploadBatchIndexRef / chunksRef — it advances them on success. Do NOT
        // re-mutate the cursors here, or remaining chunks get marked as already
        // sent (silent loss) and the batch index double-increments (server gap).
        doChunkUpload(false).catch(() => {})
      }
      if (chunksRef.current.length > 0) {
        saveChunksToIDB(meetingId, chunksRef.current, startTimeRef.current, absoluteBaseRef.current, {
          isProvisional: provisionalPendingRef.current,
          subject,
          templateId,
          uploadBatchIndex: uploadBatchIndexRef.current,
        }).catch(() => {})
      }
      // Mirror for the desktop far-end channel: flush its delta + persist its
      // buffer (with the capture offset) so a tab kill never drops system audio.
      if (remoteUpload) {
        const remoteNew = remoteUpload.chunksRef.current.slice(remoteUpload.lastUploadedChunkIndexRef.current)
        if (remoteNew.length > 0) {
          remoteUpload.doChunkUpload(false).catch(() => {})
        }
        if (remoteUpload.chunksRef.current.length > 0) {
          saveChunksToIDB(
            meetingId,
            remoteUpload.chunksRef.current,
            startTimeRef.current,
            remoteUpload.absoluteBaseRef.current,
            {
              isProvisional: provisionalPendingRef.current,
              subject,
              templateId,
              channel: "remote",
              offsetSec: remoteUpload.offsetSecRef.current,
              uploadBatchIndex: remoteUpload.uploadBatchIndexRef.current,
            },
          ).catch(() => {})
        }
      }
    }
    document.addEventListener("visibilitychange", handler)
    return () => document.removeEventListener("visibilitychange", handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chunksRef and startTimeRef are stable refs
  }, [
    state,
    meetingId,
    subject,
    templateId,
    chunksRef.current,
    lastUploadedChunkIndexRef,
    startTimeRef.current,
    uploadBatchIndexRef,
    doChunkUpload,
    remoteUpload,
  ])

  useEffect(() => {
    if (state !== "recording" && state !== "paused") return
    const handler = (): void => {
      const orgId = window.__activeOrgId
      if (!orgId) return
      // Last-ditch keepalive POST of the unsent delta for a channel. Eden Treaty
      // can't carry t.File(), so this is a raw fetch with keepalive=true.
      const beacon = (
        buf: React.MutableRefObject<Blob[]>,
        cursor: React.MutableRefObject<number>,
        batch: React.MutableRefObject<number>,
        mime: React.MutableRefObject<string>,
        channel?: "remote",
      ): void => {
        if (buf.current.length === 0) return
        const newChunks = buf.current.slice(cursor.current)
        if (newChunks.length === 0) return
        const ext = mime.current.includes("mp4") ? "mp4" : "webm"
        const form = new FormData()
        form.append("audio", new Blob(newChunks, { type: mime.current }), `beacon.${ext}`)
        form.append("chunkIndex", String(batch.current))
        form.append("isFinal", "false")
        if (channel) form.append("channel", channel)
        fetch(apiUrl(`/api/companies/${orgId}/meetings/${meetingId}/audio/chunk`), {
          method: "POST",
          body: form,
          keepalive: true,
        }).catch(() => {})
      }
      beacon(chunksRef, lastUploadedChunkIndexRef, uploadBatchIndexRef, activeMimeTypeRef)
      if (remoteUpload) {
        beacon(
          remoteUpload.chunksRef,
          remoteUpload.lastUploadedChunkIndexRef,
          remoteUpload.uploadBatchIndexRef,
          remoteUpload.activeMimeTypeRef,
          "remote",
        )
      }
    }
    window.addEventListener("pagehide", handler)
    return () => window.removeEventListener("pagehide", handler)
    // The *Ref deps are stable identities; listed to satisfy exhaustive-deps —
    // the beacon reads each `.current` live at pagehide time.
  }, [state, meetingId, chunksRef, lastUploadedChunkIndexRef, uploadBatchIndexRef, activeMimeTypeRef, remoteUpload])
}
